import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { todayISO, fmtDateDMY } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RosterTab from "@/pages/RosterTab";
import { UploadCloud, FileSpreadsheet, Users as UsersIcon } from "lucide-react";
import RoleGuide from "@/components/RoleGuide";

export default function Attendance() {
  return (
    <div className="space-y-4" data-testid="attendance-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Attendance</h1>
        <p className="text-sm text-slate-600 mt-1">Who&apos;s on-site right now, plus the monthly biometric report.</p>
      </div>
      <RoleGuide guides={{
        manager: { freq: "1× per month", title: "Upload the biometric export", text: "Once a month, export the attendance file from your fingerprint / facial-recognition machine and drop it into the 'Monthly Upload' tab. Hours-per-person are computed automatically." },
        assistant_manager: { freq: "as needed", title: "Watch who&apos;s on-site", text: "The Log tab shows real-time clock-ins. If someone forgot to clock out, you can see it here." },
        owner: { freq: "on demand", title: "Payroll cross-check", text: "The Monthly Upload tab gives you hours-per-person for any month — cross-check with the Excel your accountant uses." },
        admin: { freq: "audit", title: "Audit trail", text: "Every upload is timestamped and voidable. Old months can be re-uploaded and will overwrite." },
      }} />
      <Tabs defaultValue="log">
        <TabsList>
          <TabsTrigger value="log" data-testid="tab-att-log">Live Log</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-att-summary">Summary</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-att-monthly">Monthly Upload</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-att-roster">Roster</TabsTrigger>
        </TabsList>
        <TabsContent value="log"><LogTab /></TabsContent>
        <TabsContent value="summary"><SummaryTab /></TabsContent>
        <TabsContent value="monthly"><MonthlyTab /></TabsContent>
        <TabsContent value="roster"><RosterTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function LogTab() {
  const [rows, setRows] = useState([]);
  const [role, setRole] = useState("");
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());

  const load = async () => {
    try {
      const r = await api.get("/team/attendance/log", { params: { date_from: from, date_to: to, role: role || undefined } });
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [from, to, role]); // eslint-disable-line

  const active = rows.filter((x) => !x.clock_out_at);
  const done = rows.filter((x) => x.clock_out_at);

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex gap-3 items-end flex-wrap">
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-600">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block h-10 border border-slate-300 rounded-sm px-3 bg-white" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-600">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block h-10 border border-slate-300 rounded-sm px-3 bg-white" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-600">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="block h-10 border border-slate-300 rounded-sm px-2 bg-white">
              <option value="">All</option>
              {["owner","admin","manager","assistant_manager","cashier","filler"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {active.length > 0 && (
        <Card className="p-3 border-2 border-emerald-500 bg-emerald-50 rounded-sm">
          <h3 className="font-bold text-emerald-900 uppercase tracking-wider text-sm mb-2">On-site right now · {active.length}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {active.map((r) => (
              <div key={r.attendance_id} className="bg-white border border-emerald-300 p-2 rounded-sm">
                <div className="font-bold text-slate-900">{r.user_name}</div>
                <div className="text-xs text-slate-600">{r.role} · {r.shift_code || "—"} · in at {new Date(r.clock_in_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Attendance history</h3>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead><tr><th>Date</th><th>Name</th><th>Role</th><th>Shift</th><th>Clock In</th><th>Clock Out</th><th>Duration</th><th>Notes</th></tr></thead>
            <tbody>
              {done.map((r) => (
                <tr key={r.attendance_id}>
                  <td>{fmtDateDMY(r.date)}</td>
                  <td className="font-bold">{r.user_name}</td>
                  <td>{r.role}</td>
                  <td>{r.shift_code}</td>
                  <td className="font-mono text-xs">{new Date(r.clock_in_at).toLocaleTimeString()}</td>
                  <td className="font-mono text-xs">{new Date(r.clock_out_at).toLocaleTimeString()}</td>
                  <td className="num">{r.duration_min ? `${(r.duration_min / 60).toFixed(2)}h` : "—"}</td>
                  <td className="text-xs">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryTab() {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(todayISO());
  useEffect(() => {
    api.get("/team/attendance/summary", { params: { date_from: from, date_to: to } }).then((r) => setRows(r.data));
  }, [from, to]);
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex gap-3 items-end">
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-600">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block h-10 border border-slate-300 rounded-sm px-3 bg-white" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-600">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block h-10 border border-slate-300 rounded-sm px-3 bg-white" /></div>
        </div>
      </Card>
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Name</th><th>Role</th><th>Sessions</th><th>Hours</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id}>
                <td className="font-bold">{r.user_name}</td>
                <td>{r.role}</td>
                <td className="num">{r.sessions}</td>
                <td className="num font-bold">{r.total_hours}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


function MonthlyTab() {
  const currentMonth = () => todayISO().slice(0, 7);
  const [month, setMonth] = useState(currentMonth());
  const [file, setFile] = useState(null);
  const [uploadKind, setUploadKind] = useState("biometric");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [data, setData] = useState(null);
  const [view, setView] = useState("overview");
  const fileRef = useRef(null);

  const loadExisting = async (m) => {
    try {
      const r = await api.get("/team/attendance/monthly", { params: { month: m } });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadExisting(month); }, [month]);

  const doUpload = async () => {
    if (!file) { toast.error("Pick a .csv / .xlsx / .json file first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("month", month);
      const r = await api.post("/team/attendance/monthly_upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Accepted ${r.data.accepted} · Skipped ${r.data.skipped}`);
      loadExisting(month);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const overview = data?.overview || {};
  const summary = data?.summary || [];
  const review = data?.review || [];
  const leaves = data?.leaves || {};
  const heatmap = data?.heatmap || [];
  const corpRows = summary.filter((p) => p.is_corporate);
  const fieldRows = summary.filter((p) => !p.is_corporate);

  return (
    <div className="mt-3 space-y-3">
      {/* Upload card */}
      <Card className="p-4 border-2 border-slate-300 rounded-sm space-y-3">
        <div className="flex items-center gap-2">
          <UploadCloud size={18} className="text-slate-700" />
          <h3 className="font-bold text-slate-800">Upload the biometric export</h3>
        </div>
        <p className="text-xs text-slate-600 leading-relaxed">
          Accepts <strong>.csv</strong>, <strong>.xlsx</strong>, or your <strong>AttendanceDashboard JSON</strong> export
          (with the <code className="bg-slate-100 px-1 rounded">punches</code> array — <code className="bg-slate-100 px-1 rounded">code, name, day, status, rawIn, rawOut, normIn, normOut, durMin, otMin, flags, shiftLabel</code>).
          Re-uploading the same month overwrites the previous batch.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Month</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-11 border border-slate-300" data-testid="att-month-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">File</label>
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.json,application/json" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="att-file-input" />
              <Button type="button" variant="outline" onClick={() => { setUploadKind("biometric"); fileRef.current?.click(); }} className="h-11 border-2 border-slate-300 font-bold" data-testid="att-pick-btn">
                <FileSpreadsheet size={14} className="mr-1" /> {file ? file.name : "Choose file (biometric)"}
              </Button>
              {/* v17.9 (2026-07-22) — "Roster upload" button removed per operator
                  brief 1.6.  The roster is now BUILT IN THE APP under Attendance
                  → Roster sub-tab (10-day blocks, copy-forward, in-app select).
                  Biometric upload stays — that is machine data. */}
              <Button type="button" onClick={doUpload} disabled={busy || !file} className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold" data-testid="att-upload-btn">
                {busy ? "Uploading…" : "Upload biometric"}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Biometric = punch report from the machine. Roster is created in the app under the <strong>Roster</strong> tab (10-day blocks) — no Excel needed.
            </div>
          </div>
        </div>
        {result && (
          <div className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-sm p-3" data-testid="att-upload-result">
            <strong>Uploaded.</strong> Accepted {result.accepted} · Skipped {result.skipped} · Total rows in file {result.total_rows_seen}.
            {result.skip_reasons_sample?.length > 0 && (
              <details className="mt-1"><summary className="cursor-pointer font-bold">Why some rows were skipped ({result.skip_reasons_sample.length} sampled)</summary>
                <ul className="mt-1 ml-4 list-disc">{result.skip_reasons_sample.slice(0, 5).map((r) => <li key={r} className="text-slate-700">{r.slice(0, 120)}</li>)}</ul>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* Overview stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatChip label="Employees" value={overview.employees_total || 0} sub={`${overview.employees_corporate || 0} corp · ${overview.employees_field || 0} field`} />
        <StatChip label="Avg Attendance" value={`${overview.avg_attendance_pct || 0}%`} sub="Across working days" />
        <StatChip label="Total Hours" value={`${overview.total_hours || 0}h`} sub="Normalised, all staff" />
        <StatChip label="Records to Review" value={overview.records_to_review || 0} sub="Flagged by parser" tone={overview.records_to_review > 0 ? "warn" : "ok"} />
      </div>

      {/* View switcher */}
      <Card className="p-0 border-2 border-slate-300 rounded-sm overflow-hidden">
        <div className="flex border-b-2 border-slate-200">
          {["overview", "review", "corporate", "leaves", "heatmap"].map((v) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`att-view-${v}`}
                    className={"px-3 py-2 text-xs font-bold uppercase tracking-wider " + (view === v ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50")}>
              {v} {v === "review" && review.length > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[10px]">{review.length}</span>}
            </button>
          ))}
        </div>

        <div className="p-3">
          {summary.length === 0 && (
            <div className="text-sm text-slate-500 italic p-6 text-center border border-dashed border-slate-300 rounded-sm">
              No data for {month} yet. Upload a biometric export above.
            </div>
          )}

          {view === "overview" && summary.length > 0 && (
            <div className="overflow-x-auto">
              <table className="dense-table w-full text-sm" data-testid="att-employee-table">
                <thead>
                  <tr>
                    <th>Employee</th><th>Code</th><th>Type</th>
                    <th>Present</th><th>Absent</th><th>W/O</th><th>Leave</th>
                    <th>Total hrs</th><th>Avg/day</th><th>OT hrs</th><th>Attendance</th><th>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((p) => (
                    <tr key={p.person_name + (p.code || "")}>
                      <td className="font-bold">{p.person_name}</td>
                      <td className="font-mono text-xs">{p.code || "—"}</td>
                      <td><span className={"text-[10px] font-bold px-1.5 py-0.5 rounded-sm border " + (p.is_corporate ? "bg-purple-50 border-purple-400 text-purple-800" : "bg-emerald-50 border-emerald-400 text-emerald-800")}>{p.is_corporate ? "CORP" : "FIELD"}</span></td>
                      <td className="num">{p.days_present}</td>
                      <td className="num text-red-700">{p.days_absent || "—"}</td>
                      <td className="num">{p.days_week_off || "—"}</td>
                      <td className="num">{(p.days_leave_paid || 0) + (p.days_leave_unpaid || 0) + (p.days_half || 0) || "—"}</td>
                      <td className="num font-bold">{p.hours}</td>
                      <td className="num">{p.avg_hrs_per_present_day || "—"}</td>
                      <td className="num">{p.ot_hours || "—"}</td>
                      <td className="num"><span className={"font-bold " + (p.attendance_pct >= 85 ? "text-emerald-700" : p.attendance_pct >= 70 ? "text-amber-700" : "text-red-700")}>{p.attendance_pct}%</span></td>
                      <td className="num">{p.flagged_count > 0 ? <span className="text-red-700 font-bold">{p.flagged_count}</span> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === "review" && (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">These punches were flagged during normalisation — missing punch-out, crossed midnight, implausible duration, etc. Review with the person and confirm.</p>
              {review.length === 0 ? (
                <div className="text-sm text-slate-500 italic p-4 text-center border border-dashed border-slate-300 rounded-sm">Nothing to review — every punch looks clean.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="dense-table w-full text-sm" data-testid="att-review-table">
                    <thead><tr><th>Date</th><th>Person</th><th>Raw In</th><th>Raw Out</th><th>Norm In</th><th>Norm Out</th><th>Hours</th><th>Shift</th><th>Flags</th></tr></thead>
                    <tbody>
                      {review.map((r) => (
                        <tr key={r.attendance_id || `${r.date}-${r.person_name}`}>
                          <td>{r.date}</td>
                          <td className="font-bold">{r.person_name}</td>
                          <td className="num text-xs">{r.raw_in ?? "—"}</td>
                          <td className="num text-xs">{r.raw_out ?? "—"}</td>
                          <td className="num text-xs">{r.norm_in ?? "—"}</td>
                          <td className="num text-xs">{r.norm_out ?? "—"}</td>
                          <td className="num">{r.hours || "—"}</td>
                          <td className="text-xs">{r.shift_label || "—"}</td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {r.flags.map((f, j) => <span key={`${f}-${j}`} className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 border border-amber-400 text-amber-800 rounded-sm">{f}</span>)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {view === "corporate" && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <StatChip label="Corporate staff" value={corpRows.length} sub="Codes starting with CH" />
                <StatChip label="Avg hrs / day" value={`${overview.corp_avg_hours_per_day || 0}h`} sub="Per present day" />
                <StatChip label="Total hours" value={`${corpRows.reduce((s, p) => s + p.hours, 0).toFixed(1)}h`} sub="All corporate this month" />
              </div>
              {corpRows.length === 0 ? (
                <div className="text-sm text-slate-500 italic p-4 text-center border border-dashed border-slate-300 rounded-sm">No corporate staff detected. Codes must start with &quot;CH&quot;.</div>
              ) : (
                <table className="dense-table w-full text-sm" data-testid="att-corporate-table">
                  <thead><tr><th>Employee</th><th>Code</th><th>Present days</th><th>Total hours</th><th>Avg hrs/day</th><th>Attendance</th></tr></thead>
                  <tbody>
                    {corpRows.map((p) => (
                      <tr key={p.code || p.person_name}>
                        <td className="font-bold">{p.person_name}</td>
                        <td className="font-mono text-xs">{p.code || "—"}</td>
                        <td className="num">{p.days_present}</td>
                        <td className="num font-bold">{p.hours}</td>
                        <td className="num">{p.avg_hrs_per_present_day}</td>
                        <td className="num font-bold">{p.attendance_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {view === "leaves" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatChip label="Absent (unmarked)" value={leaves.absent_unmarked || 0} sub="Not yet classified" tone={leaves.absent_unmarked > 0 ? "warn" : "ok"} />
                <StatChip label="Paid leaves" value={leaves.paid || 0} sub="CL + SL + PL" />
                <StatChip label="Unpaid leaves" value={leaves.unpaid || 0} sub="LWP" />
                <StatChip label="Half / Holidays" value={(leaves.half || 0) + (leaves.holiday || 0)} sub={`${leaves.half || 0} half · ${leaves.holiday || 0} hol`} />
              </div>
              <table className="dense-table w-full text-sm" data-testid="att-leaves-table">
                <thead><tr><th>Employee</th><th>Absent</th><th>Paid</th><th>Unpaid</th><th>Half</th><th>Holiday</th></tr></thead>
                <tbody>
                  {summary.map((p) => (
                    <tr key={p.person_name + (p.code || "")}>
                      <td className="font-bold">{p.person_name}</td>
                      <td className="num">{p.days_absent || "—"}</td>
                      <td className="num">{p.days_leave_paid || "—"}</td>
                      <td className="num">{p.days_leave_unpaid || "—"}</td>
                      <td className="num">{p.days_half || "—"}</td>
                      <td className="num">{p.days_holiday || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === "heatmap" && heatmap.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">Daily headcount by status. Hover a cell for the exact count.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="att-heatmap-table">
                  <thead>
                    <tr>
                      <th className="text-left p-1 font-bold uppercase tracking-wider text-slate-600 text-[10px]">Date</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-emerald-700 text-[10px]">Present</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-red-700 text-[10px]">Absent</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-slate-600 text-[10px]">W/O</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-blue-700 text-[10px]">Leave</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-amber-700 text-[10px]">Half</th>
                      <th className="text-center p-1 font-bold uppercase tracking-wider text-purple-700 text-[10px]">Holiday</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmap.map((d) => (
                      <tr key={d.date} className="hover:bg-slate-50">
                        <td className="p-1 font-mono">{d.date}</td>
                        <td className="text-center p-1"><HeatCell v={d.PRESENT} c="emerald" /></td>
                        <td className="text-center p-1"><HeatCell v={d.ABSENT} c="red" /></td>
                        <td className="text-center p-1"><HeatCell v={d.WEEK_OFF} c="slate" /></td>
                        <td className="text-center p-1"><HeatCell v={(d.LEAVE_PAID || 0) + (d.LEAVE_UNPAID || 0)} c="blue" /></td>
                        <td className="text-center p-1"><HeatCell v={d.HALF} c="amber" /></td>
                        <td className="text-center p-1"><HeatCell v={d.HOLIDAY} c="purple" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function StatChip({ label, value, sub, tone = "neutral" }) {
  const cls = tone === "warn" ? "border-amber-400 bg-amber-50"
            : tone === "ok" ? "border-emerald-400 bg-emerald-50"
            : "border-slate-300 bg-white";
  return (
    <div className={"border-2 rounded-sm p-2 " + cls}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</div>
      <div className="text-xl font-bold text-slate-900 num mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function HeatCell({ v, c }) {
  if (!v) return <span className="text-slate-300">·</span>;
  const bg = { emerald: "bg-emerald-500", red: "bg-red-500", slate: "bg-slate-400", blue: "bg-blue-500", amber: "bg-amber-500", purple: "bg-purple-500" }[c] || "bg-slate-400";
  return <span className={"inline-block min-w-[22px] px-1 rounded-sm text-white text-[10px] font-bold " + bg}>{v}</span>;
}

